// 站点公开 URL 规范化工具。
//
// 目标：公开页面 URL 统一使用「尾斜杠」形式（首页保持 /），
// 让 canonical、hreflang、og:url、JSON-LD 页面 url、面包屑、sitemap 与站内 <a href> 保持一致。
//
// 排除规则（这些路径不参与尾斜杠统一，保持原样）：
// - 首页 / 保持原样；
// - /polar-finder 及其内部路径为「独立服务」；
// - /admin、/api 为站内非公开区域；
// - 资源文件（含 .webmanifest、.xml、.txt 等）、外站、tel:/mailto: 等协议链接；
// - 纯 query（?page=2）、纯锚点（#founder）。
// 内部路由查找键（React Router 的 path / slug / mock.path 查表 key）继续保持无尾斜杠，
// 导航 URL 与查表 key 解耦。

export const SITE_URL = (import.meta.env.VITE_SITE_URL || 'https://freewds.com').replace(/\/+$/, '');

/** 是否为独立服务 /polar-finder 及其内部路径。 */
function isPolarFinder(path: string): boolean {
  return path === '/polar-finder' || path.startsWith('/polar-finder/');
}

/** 是否为站内管理后台 /admin 及其内部路径。 */
function isAdmin(path: string): boolean {
  return path === '/admin' || path.startsWith('/admin/');
}

/** 是否为站内 API /api 及其内部路径。 */
function isApi(path: string): boolean {
  return path === '/api' || path.startsWith('/api/');
}

/** 拆分 query 与 hash，返回 [base, suffix]。 */
function splitQueryHash(path: string): [string, string] {
  const m = path.match(/^([^?#]*)([?#].*)?$/);
  if (!m) return [path, ''];
  return [m[1], m[2] || ''];
}

/** 资源文件后缀（含 .webmanifest 等长后缀）。 */
const RESOURCE_EXT = /\.(?:[a-zA-Z0-9]{2,5}|webmanifest)$/;

function isResourcePath(base: string): boolean {
  return RESOURCE_EXT.test(base);
}

/**
 * 是否为「同域绝对 URL」（http/https 且 origin 与 SITE_URL 精确一致）。
 * 使用 URL.origin 精确比较，避免把 https://freewds.com.evil.example/ 误判为同域。
 */
function isSameDomainAbsolute(href: string): boolean {
  if (!/^https?:\/\//i.test(href)) return false;
  try {
    return new URL(href).origin === new URL(SITE_URL).origin;
  } catch {
    return false;
  }
}

/**
 * 给公开页面路径补尾斜杠；首页保持 /；保留 query 与 hash；跳过 /polar-finder。
 * 例如：'/about' -> '/about/'；'/about#x' -> '/about/#x'；'/custom-service#custom-form' -> '/custom-service/#custom-form'。
 */
export function withTrailingSlash(path: string): string {
  const [base, suffix] = splitQueryHash(path);
  if (!base || base === '/') return '/' + suffix;
  if (isPolarFinder(base)) return base + suffix;
  if (base.endsWith('/')) return base + suffix;
  return base + '/' + suffix;
}

/** 返回完整公开 URL（带尾斜杠），例如 publicUrl('/about') -> 'https://freewds.com/about/'。 */
export function publicUrl(path: string): string {
  return `${SITE_URL}${withTrailingSlash(path)}`;
}

/**
 * 规范化站内链接：仅对「站内公开页面路径」补尾斜杠。
 * - 保留 query 与 hash；
 * - 纯 ?query、纯 #锚点、各种外部 scheme/外域、资源文件（含 .webmanifest）、
 *   /api、/admin、独立 /polar-finder 内部路径均原样返回；
 * - 同域绝对页面 URL（如 https://freewds.com/about）也应规范为尾斜杠形式。
 */
export function normalizeHref(href: string): string {
  if (!href) return href;

  // 纯 query（?page=2）或纯锚点（#founder）原样返回
  if (href.startsWith('?') || href.startsWith('#')) return href;

  // 根相对协议链接（//example.com）原样返回
  if (href.startsWith('//')) return href;

  // 同域绝对页面 URL：https://freewds.com/about -> https://freewds.com/about/
  // 必须先于通用 scheme 早退判断，否则 https://... 会被提前原样返回。
  if (isSameDomainAbsolute(href)) {
    const u = new URL(href);
    const path = u.pathname;
    // 同域资源、/api、/admin、/polar-finder 保持原样
    if (isResourcePath(path) || isPolarFinder(path) || isAdmin(path) || isApi(path)) return href;
    u.pathname = withTrailingSlash(path);
    return u.toString();
  }

  // 各种外部协议（tel:/mailto:/data:/weixin:/whatsapp: 等）原样返回
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href)) return href;

  const [base, suffix] = splitQueryHash(href);

  // 资源文件（含 .webmanifest）原样返回
  if (isResourcePath(base)) return href;

  // 独立服务 /polar-finder、站内后台 /admin、API /api 原样返回
  if (isPolarFinder(base) || isAdmin(base) || isApi(base)) return href;

  return withTrailingSlash(href);
}